import { z } from "zod";
import {
  BACKGROUND_REPLACE_GENERATION_TYPE,
  BACKGROUND_REPLACE_QUALITY_PRESETS,
  BACKGROUND_REPLACEMENT_MODES,
  LORA_GENERATION_TYPE,
  OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED,
  OBJECT_RERENDERED_QUALITY_PRESETS,
  REFRAME_GENERATION_TYPE,
} from "../../../Board/constants.ts";
import { DEFAULT_LORA_SCALE } from "./constants.ts";

const zModelBaseGenerationParams = z.object({
  uuid: z.string(),
  model_generation_type: z.undefined(),
});

export const zLoraModelExtraGenerationParams = z.object({
  scale: z.number().default(DEFAULT_LORA_SCALE),
});

const zLoraModelGenerationParams = zModelBaseGenerationParams.extend({
  model_generation_type: z.literal(LORA_GENERATION_TYPE),
  ...zLoraModelExtraGenerationParams.shape,
});

export const zReframeModelExtraGenerationParams = z.object({
  object_rerendered_quality_preset: z
    .enum([
      ...OBJECT_RERENDERED_QUALITY_PRESETS,
      OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED,
    ])
    .default(OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED),
});

export const zReframeModelGenerationParams = zModelBaseGenerationParams.extend({
  model_generation_type: z.literal(REFRAME_GENERATION_TYPE),
  ...zReframeModelExtraGenerationParams.shape,
});

export const zBackgroundReplaceModelExtraGenerationParams = z.object({
  background_replace_quality_preset: z
    .enum(BACKGROUND_REPLACE_QUALITY_PRESETS)
    .default("FAST"),
  resize_factor: z.number().default(0.5),
  background_replacement_mode: z
    .enum(BACKGROUND_REPLACEMENT_MODES)
    .default("IDEOGRAM_REPLACE_BACKGROUND"),
  is_upscaled: z.boolean().default(false),
  object_rerendered_quality_preset: z
    .enum([
      ...OBJECT_RERENDERED_QUALITY_PRESETS,
      OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED,
    ])
    .default(OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED),
});

export const zBackgroundReplaceModelGenerationParams =
  zModelBaseGenerationParams.extend({
    model_generation_type: z.literal(BACKGROUND_REPLACE_GENERATION_TYPE),
    ...zBackgroundReplaceModelExtraGenerationParams.shape,
  });

export const zModelGenerationParams = z.discriminatedUnion(
  "model_generation_type",
  [
    zModelBaseGenerationParams,
    zLoraModelGenerationParams,
    zReframeModelGenerationParams,
    zBackgroundReplaceModelGenerationParams,
  ],
);

export type ModelExtraGenerationParams = z.infer<
  | typeof zLoraModelExtraGenerationParams
  | typeof zReframeModelExtraGenerationParams
  | typeof zBackgroundReplaceModelExtraGenerationParams
>;

export const zModelsGenerationParams = z.array(zModelGenerationParams);

export type ModelGenerationParams = z.infer<typeof zModelGenerationParams>;

type ModelGenerationType =
  | typeof LORA_GENERATION_TYPE
  | typeof BACKGROUND_REPLACE_GENERATION_TYPE
  | typeof REFRAME_GENERATION_TYPE;

const MODEL_SCHEMA_MAP = {
  [LORA_GENERATION_TYPE]: zLoraModelGenerationParams,
  [REFRAME_GENERATION_TYPE]: zReframeModelGenerationParams,
  [BACKGROUND_REPLACE_GENERATION_TYPE]: zBackgroundReplaceModelGenerationParams,
} as const;

// XXX: We could group those funtions and specify the number of models in the array
export const createModelGenerationParamsSchema = (
  modelGenerationTypes: ModelGenerationType[],
) => {
  if (modelGenerationTypes.length === 0) return zModelBaseGenerationParams;

  const selectedSchemas: any[] = [zModelBaseGenerationParams];

  for (const type of modelGenerationTypes) {
    if (type in MODEL_SCHEMA_MAP) selectedSchemas.push(MODEL_SCHEMA_MAP[type]);
  }

  return z.discriminatedUnion("model_generation_type", selectedSchemas as any);
};

export const createModelsGenerationParamsSchema = (
  modelGenerationTypes: ModelGenerationType[],
) => {
  const singleSchema = createModelGenerationParamsSchema(modelGenerationTypes);
  return z.array(singleSchema);
};
